import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User } from '@/types'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean

  setUser: (user: User | null) => void
  setTokens: (access: string, refresh: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
}

// Custom storage that checks rememberMe preference
const getStorage = () => {
  const rememberMe = typeof window !== 'undefined' && localStorage.getItem('rememberMe') === 'true'
  return rememberMe ? localStorage : sessionStorage
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setTokens: (access, refresh) =>
        set({
          accessToken: access,
          refreshToken: refresh,
          isAuthenticated: true,
        }),

      logout: () => {
        // Clear auth tokens but preserve remember me settings
        // The remember me email/checkbox state should persist across logouts
        localStorage.removeItem('auth-storage')
        sessionStorage.removeItem('auth-storage')
        sessionStorage.removeItem('sessionAuth')
        // Note: We intentionally do NOT remove:
        // - login_remember_me (checkbox state)
        // - login_saved_email (saved email)
        // - rememberMe (auth store flag - cleared only when user unchecks)
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => getStorage()),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
